import { Card, CardContent, Typography, Box } from "@mui/material";
import { Repeat } from "@mui/icons-material";

export interface RepeatPurchaseRateCardProps {
  rates: {
    weekly: number;
    monthly: number;
    quarterly: number;
  };
}

export function RepeatPurchaseRateCard({ rates }: RepeatPurchaseRateCardProps) {
  const formatRate = (rate: number) => `${rate.toFixed(1)}%`;

  const getRateColor = (rate: number) => {
    if (rate >= 50) return "success.main";
    if (rate >= 30) return "warning.main";
    return "error.main";
  };

  const periods = [
    { label: "Weekly", value: rates.weekly, period: "Last 7 days" },
    { label: "Monthly", value: rates.monthly, period: "Last 30 days" },
    { label: "Quarterly", value: rates.quarterly, period: "Last 90 days" },
  ];

  return (
    <Card>
      <CardContent>
        {/* Header */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}>
          <Repeat color="primary" />
          <Typography variant="h6">
            Repeat Purchase Rate
          </Typography>
        </Box>

        {/* Three columns for different time periods */}
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
          {periods.map((period, index) => (
            <Box key={index} sx={{ flex: "1 1 30%", minWidth: "200px" }}>
              <Box
                sx={{
                  p: 2,
                  border: 1,
                  borderColor: "divider",
                  borderRadius: 2,
                  textAlign: "center",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    borderColor: getRateColor(period.value),
                    boxShadow: 2,
                  },
                }}
              >
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                  {period.label}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 700,
                    color: getRateColor(period.value),
                    mb: 0.5,
                  }}
                >
                  {formatRate(period.value)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {period.period}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>

        {/* Explanation */}
        <Box sx={{ mt: 3, p: 2, bgcolor: "action.hover", borderRadius: 1 }}>
          <Typography variant="caption" color="text.secondary">
            💡 <strong>Note:</strong> Percentage of customers who made 2+ purchases in the period.
          </Typography>
        </Box>

        {/* Color Legend */}
        <Box sx={{ display: "flex", justifyContent: "center", gap: 3, mt: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Box sx={{ width: 12, height: 12, bgcolor: "success.main", borderRadius: "50%" }} />
            <Typography variant="caption">
              Excellent (≥50%)
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Box sx={{ width: 12, height: 12, bgcolor: "warning.main", borderRadius: "50%" }} />
            <Typography variant="caption">
              Average (30-50%)
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Box sx={{ width: 12, height: 12, bgcolor: "error.main", borderRadius: "50%" }} />
            <Typography variant="caption">
              Low (&lt;30%)
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}
